<template>
  <div class="atom-line-chart__card">
    <div
      class="atom-line-chart__header"
      :class="[headerList.length > 5 ? `grid-3` : `grid-${headerList.length}`]"
    >
      <div
        v-for="({ name, itemColor }, index) in headerList"
        :key="index"
        class="grid-item d-flex align-items-center"
      >
        <span
          class="header__dot"
          :style="`backgroundColor:${itemColor}`"
        ></span>
        <label class="header__text mb-0">{{ name }}</label>
      </div>
    </div>

    <div class="atom-line-chart_graph">
      <v-chart
        class="graph__echarts"
        autoresize
        :option="option"
        ref="line"
        auto-resize
      />
    </div>
  </div>
</template>

<script>
import { use } from "echarts/core"

// import ECharts modules manually to reduce bundle size
import { CanvasRenderer } from "echarts/renderers"
import { LineChart } from "echarts/charts"
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components"
import VChart from "vue-echarts"

use([
  CanvasRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
])

import { DateTime } from "luxon"
import { BCard } from "bootstrap-vue"
export default {
  name: "AtomMultiLineChart",
  components: { BCard, VChart },
  props: {
    dataList: {
      type: Array,
      required: true,
      // example: [
      //   {
      //     name: "A",
      //     color: "#1ABC9C",
      //     itemColor: "#AAE6DA",
      //     data: [28, 12, 27, 8, 22, 8, 14],
      //   },
      // ]
    },
    timestampList: {
      type: Array,
      required: true,
      // example: ["00", "02", "04", "06", "08", "10", "12"]
    },
  },
  data() {
    return {
      option: {},
    }
  },
  computed: {
    headerList() {
      return this.dataList.map(({ name, itemColor }) => ({ name, itemColor }))
    },
    valueList() {
      return this.dataList.map(({ name, color, itemColor, data }) => ({
        name,
        type: "line",
        smooth: true,
        showSymbol: false,
        lineStyle: {
          width: 4,
          color,
          opacity: 0.5,
        },
        itemStyle: {
          color: itemColor,
        },
        data,
      }))
    },
  },
  watch: {
    dataList: {
      deep: true,
      handler() {
        this.initialEcharts()
      },
    },
  },
  mounted() {
    this.initialEcharts()
  },
  methods: {
    initialEcharts() {
      this.option = {
        tooltip: {
          trigger: "axis",
        },
        grid: {
          top: "15",
          left: "5",
          right: "5",
          bottom: "0",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          axisTick: {
            show: false,
          },
          axisLine: {
            show: false,
          },
          axisLabel: {
            color: "#B9B9C3",
          },
          data: this.timestampList,
        },
        yAxis: {
          type: "value",
          axisLabel: {
            color: "#B9B9C3",
          },
          splitLine: {
            lineStyle: {
              type: "solid",
              width: 3,
              color: "#EBE9F1",
              opacity: 0.5,
            },
          },
        },
        series: this.valueList,
      }
    },
    formatTime(datetime) {
      return DateTime.fromISO(datetime).toFormat("dd MMMM yyyy HH:mm")
    },
  },
}
</script>

<style lang="scss" scoped>
.card {
  margin-bottom: 0;
}

.atom-line-chart__card {
  width: 100%;
}

// graph
.atom-line-chart_graph {
  width: 100%;
}
.graph__echarts {
  width: 100%;
  height: 240px;
}

.atom-line-chart__header {
  display: grid;
  gap: 14px;
  justify-content: center;
  margin-bottom: 25px;
}

.grid-1 {
  grid-template-columns: minmax(auto, 155px);
}

.grid-2 {
  grid-template-columns: repeat(2, minmax(auto, 155px));
}
.grid-3 {
  grid-template-columns: repeat(2, minmax(auto, 155px));
}
.grid-4 {
  grid-template-columns: repeat(4, minmax(auto, 155px));
}

.grid-5 {
  grid-template-areas:
      "i1 i1 i1 i2 i2 i2"
      "i3 i3 i3 i4 i4 i4"
      ". . i5 i5 . .";

  @for $i from 1 through 5 {
    .grid-item:nth-child(#{$i}) {
      grid-area: i#{$i};
    }
  }
}

.header__dot {
  height: 12px;
  width: 12px;
  background-color: #bbb;
  border-radius: 50%;
  display: inline-block;
  margin-right: 16px;
}

.header__text {
  color: #5e5873;
  font-weight: 500;
  font-size: 14px;
}

.break {
  flex-basis: 100%;
  height: 0;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
  .grid-3 {
    grid-template-columns: repeat(3, minmax(auto, 155px));
  }

  .grid-5 {
    grid-template-areas:
    "i1 i1 i2 i2 i3 i3"
    ". i4 i4 i5 i5 .";
  }
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
}
</style>
